import React from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  Shield, 
  Armchair, 
  CircleDot, 
  Layers, 
  Lightbulb, 
  Gauge, 
  Wind, 
  Truck,
  ArrowRight 
} from 'lucide-react';

const coreServices = [
  {
    icon: Sparkles,
    title: 'Premium Auto Detailing',
    description: 'Complete exterior and interior detailing using premium products for a showroom finish.',
    image: '/images/gallery/1-after.png',
  },
  {
    icon: Shield,
    title: 'Paint Protection',
    description: 'PPF and ceramic coating solutions to protect your vehicle\'s paint from damage.',
    image: '/images/service-ppf-v2.png',
  },
  {
    icon: Armchair,
    title: 'Interior Restoration',
    description: 'Deep cleaning and restoration of leather, fabric, and all interior surfaces.',
    image: '/images/gallery/3-after.png',
  },
  {
    icon: CircleDot,
    title: 'Alloy Wheels & Tyres',
    description: 'Professional wheel refurbishment, powder coating, and tire services.',
    image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=600&q=80',
  },
];

const customizationServices = [
  {
    icon: Layers,
    title: 'Body Kits & Conversions',
    description: 'Transform your vehicle with Fortuner to Legender, Thar builds, and Defender style conversions.',
    image: '/images/gallery/2-after.png',
  },
  {
    icon: Lightbulb,
    title: 'Custom Lighting',
    description: 'LED upgrades, ambient lighting, and custom headlight/taillight modifications.',
    image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=600&q=80',
  },
  {
    icon: Armchair,
    title: 'Interior Upgrades',
    description: 'Custom upholstery, dashboard modifications, and premium interior enhancements.',
    image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600&q=80',
  },
];

const performanceServices = [
  {
    icon: Gauge,
    title: 'ECU Tuning & Remaps',
    description: 'Unlock your engine\'s potential with professional ECU tuning and performance remaps.',
    image: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=600&q=80',
  },
  {
    icon: Wind,
    title: 'Turbo Upgrades',
    description: 'High-performance turbocharger upgrades for maximum power gains.',
    image: 'https://images.unsplash.com/photo-1489824904134-891ab64532f1?w=600&q=80',
  },
  {
    icon: Truck,
    title: 'Suspension Upgrades',
    description: 'Street, off-road, and air suspension solutions for improved handling and stance.',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
  },
];

const ServiceCard = ({ service, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className="group glass-card overflow-hidden hover-lift"
  >
    <div className="relative h-48 overflow-hidden">
      <img
        src={service.image}
        alt={service.title}
        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent" />
      <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-primary/20 backdrop-blur-sm flex items-center justify-center">
        <service.icon className="w-6 h-6 text-primary" />
      </div>
    </div>
    <div className="p-6">
      <h4 className="text-xl font-semibold text-white mb-3 group-hover:text-primary transition-colors">
        {service.title}
      </h4>
      <p className="text-gray-400 text-sm leading-relaxed mb-4">
        {service.description}
      </p>
    </div>
  </motion.div>
);

const ServicesPage = () => {
  return (
    <div className="pt-24 pb-20 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-primary uppercase tracking-[0.3em] text-sm font-medium mb-4 block">
            What We Offer
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Our Services<span className="text-primary">.</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            From premium detailing to complete vehicle transformations, we offer
            comprehensive automotive services tailored to your needs.
          </p>
        </motion.div>

        {/* Doorstep Service Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-primary/20 to-primary/5 border border-primary/30 rounded-2xl p-8 mb-16 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
              <Truck className="w-8 h-8 text-black" />
            </div>
            <div>
              <h3 className="text-2xl font-display font-bold text-white mb-1">
                Doorstep Detailing Services
              </h3>
              <p className="text-gray-400">
                Premium automotive care delivered to your location. Same quality, ultimate convenience.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Core Services */}
        <div className="mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-display font-bold text-white mb-8 flex items-center"
          >
            <span className="w-8 h-px bg-primary mr-4"></span>
            Core Services
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreServices.map((service, index) => (
              <ServiceCard key={service.title} service={service} index={index} />
            ))}
          </div>
        </div>

        {/* Customization Services */}
        <div className="mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-display font-bold text-white mb-8 flex items-center"
          >
            <span className="w-8 h-px bg-primary mr-4"></span>
            Customization
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {customizationServices.map((service, index) => (
              <ServiceCard key={service.title} service={service} index={index} />
            ))}
          </div>
        </div>

        {/* Performance Services */}
        <div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl font-display font-bold text-white mb-8 flex items-center"
          >
            <span className="w-8 h-px bg-primary mr-4"></span>
            Performance (Phase 2)
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {performanceServices.map((service, index) => (
              <ServiceCard key={service.title} service={service} index={index} />
            ))}
          </div>
        </div>

        {/* Our Process */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-24"
        >
          <div className="text-center mb-12">
            <span className="text-primary uppercase tracking-[0.3em] text-sm font-medium">
              How It Works
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mt-4">
              Our Process
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Discuss your vision and requirements with our experts' },
              { step: '02', title: 'Assessment', desc: 'Thorough vehicle inspection and personalized recommendations' },
              { step: '03', title: 'Execution', desc: 'Precision work by our skilled technicians' },
              { step: '04', title: 'Delivery', desc: 'Quality check and handover of your transformed vehicle' },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-primary flex items-center justify-center mx-auto mb-6">
                  <span className="text-black font-bold text-xl">{item.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-24"
        >
          <div className="text-center mb-12">
            <span className="text-primary uppercase tracking-[0.3em] text-sm font-medium">
              Common Questions
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mt-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              { q: 'How long does ceramic coating last?', a: 'Our premium ceramic coatings last 2-5 years depending on maintenance and environmental factors.' },
              { q: 'Do you offer warranty on services?', a: 'Yes, all our services come with warranty coverage. PPF has 5-10 year warranties.' },
              { q: 'Can I get doorstep service?', a: 'Absolutely! We offer premium doorstep detailing services across Hyderabad.' },
              { q: 'How long does a full detail take?', a: 'A complete detailing service typically takes 1-3 days depending on vehicle condition.' },
              { q: 'Do you work on all vehicle types?', a: 'Yes, we service all cars, SUVs, and premium vehicles from all manufacturers.' },
              { q: 'What payment options do you accept?', a: 'We accept cash, cards, UPI, and bank transfers for your convenience.' },
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6"
              >
                <h4 className="text-white font-semibold mb-2">{faq.q}</h4>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ServicesPage;
